import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Search, Activity, PowerOff, Signal, Network, Radio, Usb,
  AlertCircle, CheckCircle2, Loader2, RefreshCw, Ear,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import espnowService, {
  type DiagnosticAction, type DiagnosticResult, type MeshTopology, type EspNowRouter,
  type NodeInventory, type FieldCommand,
} from '../../services/espnowIrrigation';
import { errorMessage } from '../../utils/apiResponse';
import { nodeNoun } from '../../utils/nodeLabel';
import RecentActivity from './RecentActivity';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';

/**
 * The interrogation half of the original browser tool, for an XBee site.
 *
 * Every action here is asynchronous in a way the UI has to be honest about: it
 * queues a command, the bridge polls for it within ~15s, then runs it against
 * the radio. Some of them take minutes: a discovery unicasts every node it
 * already knows and then broadcasts up to three times over a window the radio
 * itself dictates, and a mesh scan walks every node in turn. So there is no
 * synchronous result to show.
 *
 * There is, however, an eventual one. The bridge has always written an outcome
 * for every command it ran — "0/6 nodes reported state", "the coordinator is
 * disconnected" — and nothing here read it, so the panel could only say
 * "queued, refresh to see the result" while the node list refreshed to look
 * identical. That is how an operator concludes the buttons do nothing. Recent
 * checks below is that answer, polled until it settles.
 *
 * The other half of the same problem is the coordinator banner. Every action
 * except CHECK_COORDINATOR talks THROUGH the radio, so all of them fail
 * identically whether the radio is gone or the nodes are — and only one of
 * those is worth a drive out to check node batteries.
 */

interface Props {
  controllerId: string;
  transport?: string | null;
  routers: EspNowRouter[];
  online: boolean;
  /**
   * Coordinator link state, straight off the controller row. `null` means the
   * bridge has never reported it — an older build, so the panel must not claim
   * the radio is missing on that evidence alone.
   */
  coordinatorConnected?: boolean | null;
  coordinatorLastOk?: string | null;
  coordinatorError?: string | null;
  /** Refetch the node list after a diagnostic has had time to land. */
  onRefresh?: () => void;
  /**
   * Everything else this site has been asked to do — valve runs, forced
   * programs, node removals, bridge updates. Already fetched by the page, so
   * this panel borrows it rather than polling the same endpoint twice.
   */
  commands?: FieldCommand[];
  /** Jump to the Events tab, where the command log is shown in full. */
  onShowLog?: () => void;
}

interface ActionSpec {
  action: DiagnosticAction;
  label: string;
  icon: React.ReactNode;
  hint: string;
  /** Needs at least one known node to act on. */
  needsNodes?: boolean;
  danger?: boolean;
  body?: Record<string, unknown>;
}

export default function MeshDiagnostics({
  controllerId, transport, routers, online,
  coordinatorConnected, coordinatorLastOk, coordinatorError, onRefresh,
  commands = [], onShowLog,
}: Props) {
  const noun = nodeNoun(transport);
  const tz = useOrgTimezone();

  const [busy, setBusy] = useState<DiagnosticAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState<string | null>(null);
  const [topology, setTopology] = useState<MeshTopology | null>(null);
  const [loadingTopo, setLoadingTopo] = useState(false);
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [inventory, setInventory] = useState<NodeInventory | null>(null);
  /**
   * Exhaustive discovery. Off by default: it borrows the radio's NT parameter at
   * its maximum for the duration of the sweep, and NT governs how the radio
   * behaves for everything else, so it is not a thing to leave switched on.
   */
  const [deepSearch, setDeepSearch] = useState(false);
  /** Whether a mesh scan was still outstanding at the last poll. */
  const scanPending = useRef(false);
  /** Same, for a discovery — its result is the inventory below. */
  const discoveryPending = useRef(false);

  const loadTopology = useCallback(async () => {
    setLoadingTopo(true);
    try {
      setTopology(await espnowService.getMeshTopology(controllerId));
    } catch {
      // A missing topology is not worth an error banner - it just means no
      // scan has run yet, which the empty state already communicates.
    } finally {
      setLoadingTopo(false);
    }
  }, [controllerId]);

  const loadInventory = useCallback(async () => {
    try {
      setInventory(await espnowService.getNodeInventory(controllerId));
    } catch {
      // No inventory yet is the state of every site that has not run a
      // discovery since the bridge was updated. The empty state covers it.
    }
  }, [controllerId]);

  const loadResults = useCallback(async () => {
    try {
      const next = await espnowService.getDiagnosticResults(controllerId, 8);
      setResults(next);
      // The link map can only have changed when a scan finishes, and how long
      // that takes depends on how many nodes there are — a fixed timer either
      // refetches the old map or leaves a finished scan looking unrun. The
      // scan's own result settling is the signal, so use that.
      const pending = next.some(r => r.action === 'SCAN_MESH' && r.tone === 'pending');
      if (scanPending.current && !pending) loadTopology();
      scanPending.current = pending;

      // Same reasoning for the inventory: how long a discovery takes depends on
      // the node count and the radio's own NT, so its result settling is the
      // only honest signal that there is a new one to fetch.
      const discovering = next.some(
        r => r.action === 'DISCOVER_NODES' && r.tone === 'pending');
      if (discoveryPending.current && !discovering) {
        loadInventory();
        // A discovery that enrolled something changed the node list itself.
        onRefresh?.();
      }
      discoveryPending.current = discovering;
    } catch {
      // Same reasoning as the topology: no history is a normal state.
    }
  }, [controllerId, loadTopology, loadInventory, onRefresh]);

  useEffect(() => {
    loadTopology(); loadResults(); loadInventory();
  }, [loadTopology, loadResults, loadInventory]);

  // Poll while anything is outstanding, and stop as soon as nothing is. The
  // whole complaint these results answer is "the button does nothing", so the
  // answer has to arrive without the operator knowing to press refresh — but a
  // panel that keeps polling a settled site forever is its own problem.
  useEffect(() => {
    if (!results.some(r => r.tone === 'pending')) return;
    const timer = setInterval(loadResults, 10000);
    return () => clearInterval(timer);
  }, [results, loadResults]);

  // The radio is missing, and the bridge is new enough to say so. `null` is
  // deliberately not this: an older bridge reports nothing, and treating
  // silence as a fault would put a red banner on every healthy legacy site.
  const radioDown = coordinatorConnected === false;

  const ACTIONS: ActionSpec[] = [
    {
      action: 'CHECK_COORDINATOR',
      label: 'Check Coordinator',
      icon: <Usb className="w-4 h-4" />,
      hint: 'Asks the local radio for its firmware, network and address. Run this first — '
        + 'every other check here talks THROUGH the coordinator, so they all fail the same '
        + `way whether the radio is missing or the ${noun.manyLower} are.`,
    },
    {
      action: 'DISCOVER_NODES',
      label: `Find ${noun.many}`,
      icon: <Search className="w-4 h-4" />,
      hint: `Asks every known ${noun.oneLower} directly, then broadcasts for ones nobody `
        + `has added yet. Names any ${noun.oneLower} that answered nothing. Takes up to a `
        + `few minutes on a large or slow mesh.`,
    },
    {
      action: 'READ_STATES',
      label: 'Check State',
      icon: <Activity className="w-4 h-4" />,
      hint: 'Reads the live output pins of every known node.',
      needsNodes: true,
    },
    {
      action: 'READ_SIGNAL',
      label: 'Read Signal',
      icon: <Signal className="w-4 h-4" />,
      hint: 'Reads RSSI from each node so the signal column is current.',
      needsNodes: true,
    },
    {
      action: 'SCAN_MESH',
      label: 'Scan Mesh',
      icon: <Network className="w-4 h-4" />,
      hint: 'Asks every node for its neighbours and builds the link map below. '
        + 'On radios that cannot answer, falls back to the hops the mesh itself '
        + 'reports. Takes several seconds per node.',
      needsNodes: true,
    },
    {
      action: 'SET_BROADCASTING',
      label: 'Enable Reporting',
      icon: <Radio className="w-4 h-4" />,
      hint: 'Turns on periodic IO sampling and writes it to flash so it survives a power cycle.',
      needsNodes: true,
      body: { enabled: true, interval_ms: 5000 },
    },
    {
      action: 'ALL_OFF',
      label: 'All Off',
      icon: <PowerOff className="w-4 h-4" />,
      hint: 'Drives every relay on every node low. Stops all watering immediately.',
      needsNodes: true,
      danger: true,
    },
  ];

  async function run(spec: ActionSpec) {
    if (spec.danger && !confirm(
      `Turn OFF every relay on all ${routers.length} ${noun.manyLower}? This stops any watering in progress.`
    )) return;

    setBusy(spec.action);
    setError(null);
    setQueued(null);
    try {
      await espnowService.runDiagnostic(controllerId, {
        action: spec.action,
        ...(spec.body || {}),
        // Only discovery has anything to do with it, and sending it on other
        // actions would be rejected by the server's payload schema.
        ...(spec.action === 'DISCOVER_NODES' && deepSearch ? { deep: true } : {}),
      });
      setQueued(
        `${spec.label} queued. The bridge picks it up within about 15 seconds; ` +
        `the result appears under "Recent checks" below.`
      );
      // Pull the new QUEUED row in straight away so there is visible evidence
      // the press landed; loadResults refetches the link map by itself once
      // the scan settles, however long the walk takes.
      loadResults();
    } catch (err) {
      setError(errorMessage(err, `Could not queue ${spec.label}`));
    } finally {
      setBusy(null);
    }
  }

  const noNodes = routers.length === 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Network className="w-5 h-5" />
          Mesh diagnostics
        </CardTitle>
        {onRefresh && (
          <Button variant="ghost" size="sm" onClick={onRefresh} aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {!online && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              This controller is offline. Commands will queue and run when the bridge reconnects.
            </span>
          </div>
        )}

        {/* The radio, as distinct from the laptop. Shown whenever the bridge is
            reporting link state at all, because "coordinator connected" is the
            fact that makes an offline node list trustworthy — without it, every
            reading below is the last known value rather than a live one. */}
        {online && radioDown && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
            <Usb className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">The XBee coordinator is not connected.</p>
              <p className="mt-1">
                The laptop is online and reporting, but it cannot reach the mesh, so
                every {noun.oneLower} below is showing its last known state rather than a
                live one — they are not necessarily offline. Check the USB radio is
                plugged in and in API mode.
              </p>
              {coordinatorError && (
                <p className="mt-1 font-mono text-xs break-words">{coordinatorError}</p>
              )}
              {coordinatorLastOk && (
                <p className="mt-1 text-xs">
                  Last working {formatDateTimeInTz(coordinatorLastOk, tz)}.
                </p>
              )}
            </div>
          </div>
        )}

        {online && coordinatorConnected === true && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
            <Usb className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Coordinator connected. Any {noun.oneLower} showing offline below is genuinely
              not answering — run <strong>Check Coordinator</strong> to confirm the radio is
              also on its network.
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {queued && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-800">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{queued}</span>
          </div>
        )}

        {/* Exhaustive discovery. Its own control rather than a separate button,
            because it is not a different action - it is the same discovery given
            the radio's maximum discovery window. Worth reaching for on a site
            several hops deep, where the far end of the mesh answers too late to
            be heard inside the default window. */}
        <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={deepSearch}
            onChange={e => setDeepSearch(e.target.checked)}
            className="mt-0.5 rounded border-gray-300"
          />
          <span>
            <span className="font-medium">Exhaustive search</span>
            <span className="text-gray-500">
              {' '}— gives <strong>Find {noun.many}</strong> the radio's longest discovery
              window, then puts it back. Slower; use it when a {noun.oneLower} several hops
              away is not being found.
            </span>
          </span>
        </label>

        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {ACTIONS.map(spec => {
            const disabled = busy !== null || (spec.needsNodes && noNodes);
            return (
              <button
                key={spec.action}
                onClick={() => run(spec)}
                disabled={disabled}
                title={spec.needsNodes && noNodes
                  ? `Run "Find ${noun.many}" first`
                  : spec.hint}
                className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left transition
                  disabled:opacity-50 disabled:cursor-not-allowed
                  ${spec.danger
                    ? 'border-red-200 hover:bg-red-50 text-red-700'
                    : 'border-gray-200 hover:bg-gray-50 text-gray-800'}`}
              >
                <span className="flex items-center gap-2 font-medium text-sm">
                  {busy === spec.action
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : spec.icon}
                  {spec.label}
                </span>
                <span className="text-xs text-gray-500 leading-snug">{spec.hint}</span>
              </button>
            );
          })}
        </div>

        {/* What the last few presses actually did, and what else this site has
            been asked to do since. The bridge has always written an outcome for
            every command it ran; nothing in the UI read it, so the panel could
            only say "queued" and an operator watching an unchanged node list
            concluded the buttons were dead. This is that answer, surfaced — and
            not only for the checks above, because the valve run or forced
            program that did not happen is usually what somebody is standing
            there asking about. */}
        {(results.length > 0 || commands.length > 0) && (
          <div className="pt-2 border-t border-gray-200">
            <RecentActivity
              results={results}
              commands={commands}
              routers={routers}
              transport={transport}
              onRefresh={() => { loadResults(); onRefresh?.(); }}
              onShowLog={onShowLog}
            />
          </div>
        )}

        {/* What the last discovery concluded, as data rather than as prose.
            "found 4 nodes" was the whole answer before this, and it is the wrong
            shape: it counts who shouted back inside a broadcast window and
            cannot say that node 5 is missing, because a broadcast never knew
            node 5 was supposed to be there. The bridge now reports both halves,
            and the silent list is the half somebody has to act on. */}
        {inventory && (
          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Last inventory</h4>
              <span className="text-xs text-gray-500">
                {formatDateTimeInTz(inventory.timestamp, tz)}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                {inventory.answered.length} of {inventory.known} answered
              </span>
              {inventory.missing.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  {inventory.missing.length} silent
                </span>
              )}
              {inventory.new.length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                  {inventory.new.length} newly added
                </span>
              )}
              {inventory.passes > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {inventory.passes} pass{inventory.passes === 1 ? '' : 'es'}
                  {inventory.window_sec ? ` of ${inventory.window_sec}s` : ''}
                </span>
              )}
            </div>

            {inventory.missing.length > 0 && (
              <div className="p-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-800">
                <p className="font-medium">
                  Answered neither a direct request nor the broadcast:
                </p>
                <ul className="mt-1 space-y-0.5">
                  {inventory.missing.map(mac => (
                    <li key={mac} className="font-mono break-all">
                      {nameFor(mac, routers)}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 font-sans">
                  The bridge asked each of these twice and they said nothing, so check
                  power, range and aerials. If they are ALL silent, run{' '}
                  <strong>Check Coordinator</strong> first — a radio that is present but
                  off its network looks exactly like this.
                </p>
              </div>
            )}

            {inventory.candidates.length > 0 && (
              <p className="mt-2 flex items-start gap-2 text-xs text-amber-800">
                <Ear className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  {inventory.candidates.length} address
                  {inventory.candidates.length === 1 ? '' : 'es'} heard on this site
                  {' '}but not added — see <strong>Add {noun.manyLower}</strong> below.
                </span>
              </p>
            )}
          </div>
        )}

        {/* Link map from the last mesh scan */}
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-gray-700">Link map</h4>
            {topology && (
              <span className="text-xs text-gray-500">
                Scanned {formatDateTimeInTz(topology.scanned_at, tz)}
              </span>
            )}
          </div>

          {loadingTopo ? (
            <p className="text-sm text-gray-500 py-2">Loading…</p>
          ) : !topology || topology.links.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">
              No scan yet. Run <strong>Scan Mesh</strong> to map which {noun.manyLower} can
              hear each other and how strongly.
            </p>
          ) : (
            <div className="overflow-x-auto">
              {/* Not every radio can answer "who can you hear?". Without FN the
                  bridge falls back twice over — first to the hops the network
                  layer reports, then to each node's signal to the coordinator
                  — and all three maps are indistinguishable in a table of
                  links. Which one this is has to be on the page, or an
                  operator reads a star as though it were measured hops.

                  Route records are amber-free on purpose: they are a real
                  topology, not a degraded one, and warning about them would
                  push someone towards replacing hardware that is working. */}
              {topology.method === 'route-records' && (
                <p className="mb-2 text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                  This radio's firmware cannot report neighbour tables, so these hops come from
                  the mesh's own routing instead. The links are real; the signal column is empty
                  because routing tables do not carry signal strength.
                </p>
              )}
              {topology.method === 'coordinator-signal' && (
                <p className="mb-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                  This radio's firmware cannot report neighbour tables and no {noun.oneLower} reported
                  a route, so these are signal strengths to the coordinator rather than{' '}
                  {noun.oneLower}-to-{noun.oneLower} links.
                </p>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">From</th>
                    <th className="py-2 pr-4">To</th>
                    <th className="py-2 pr-4">Signal</th>
                    <th className="py-2">Quality</th>
                  </tr>
                </thead>
                <tbody>
                  {topology.links.map((link, i) => (
                    <tr key={`${link.from}-${link.to}-${i}`} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-mono text-xs">{nameFor(link.from, routers)}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {link.to_name || nameFor(link.to, routers)}
                      </td>
                      {/* FN can name a neighbour without reporting a signal for
                          it. An em dash is the honest rendering; "0 dBm" would
                          read as a perfect link. */}
                      <td className="py-2 pr-4">
                        {link.rssi_dbm === null ? '—' : `${link.rssi_dbm} dBm`}
                      </td>
                      <td className="py-2">
                        {link.quality && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${QUALITY_TONE[link.quality]}`}>
                            {link.quality}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const QUALITY_TONE: Record<string, string> = {
  excellent: 'bg-green-100 text-green-700',
  good: 'bg-emerald-100 text-emerald-700',
  fair: 'bg-amber-100 text-amber-700',
  poor: 'bg-red-100 text-red-700',
};

/** Prefer the operator-facing name; fall back to the address it came in as. */
function nameFor(mac: string, routers: EspNowRouter[]): string {
  if (mac === 'coordinator') return 'Coordinator';
  const match = routers.find(r => r.router_mac?.toUpperCase() === mac.toUpperCase());
  return match?.name || mac;
}
